/**
 * Base Map Details Component
 * 
 * Displays complete base map metadata with version history and actions.
 * Requirements: 40.2, 40.3
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  ColumnLayout,
  Box,
  Button,
  StatusIndicator,
  Alert,
  KeyValuePairs,
  Table
} from '@cloudscape-design/components';
import { baseMapsApi } from '../../services/baseMapsApi';
import type { BaseMap, BaseMapVersion } from '../../types/baseMaps';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const BaseMapDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [baseMap, setBaseMap] = useState<BaseMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<BaseMapVersion | null>(null);

  useEffect(() => {
    if (id) {
      loadBaseMap(id);
    }
  }, [id]);

  const loadBaseMap = async (baseMapId: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await baseMapsApi.getBaseMap(baseMapId);
      
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        setBaseMap(response.data.baseMap);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load base map');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      const response = await baseMapsApi.deleteBaseMap(id);
      
      if (response.error) {
        setError(response.error);
      } else {
        navigate('/admin/base-maps');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete base map');
    }
  };

  const handleTestInPrintMapMaker = () => {
    if (!baseMap) return;
    
    const params = new URLSearchParams({
      baseMapId: baseMap.id,
      baseMapName: baseMap.name,
      baseMapType: baseMap.type
    });
    window.open(`/print-map-maker?${params.toString()}`, '_blank');
  };

  const handleRollback = async () => {
    if (!id || !selectedVersion) return;

    try {
      // Rollback creates a new version with the old configuration
      // Requirements: 40.3, 40.4, 40.5
      const response = await baseMapsApi.updateBaseMap(id, selectedVersion.changes);
      
      if (response.error) {
        setError(response.error);
      } else {
        // Reload the base map to show the new version
        await loadBaseMap(id);
        setShowRollbackModal(false);
        setSelectedVersion(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rollback base map');
    }
  };

  const handleRollbackClick = (version: BaseMapVersion) => {
    setSelectedVersion(version);
    setShowRollbackModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getProjectionTypeName = (type: string) => {
    const typeMap: Record<string, string> = {
      lcc: 'Lambert Conformal Conic',
      mercator: 'Mercator',
      albers: 'Albers Equal Area',
      utm: 'UTM'
    };
    return typeMap[type] || type;
  };

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <StatusIndicator type="loading">Loading base map details...</StatusIndicator>
      </Box>
    );
  }

  if (error || !baseMap) {
    return (
      <Alert type="error" header="Error loading base map">
        {error || 'Base map not found'}
      </Alert>
    );
  }

  return (
    <>
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={handleTestInPrintMapMaker}>
                Test in Print Map Maker
              </Button>
              <Button onClick={() => navigate(`/admin/base-maps/${id}/edit`)}>
                Edit
              </Button>
              <Button onClick={() => setShowDeleteModal(true)}>
                Delete
              </Button>
            </SpaceBetween>
          }
        >
          {baseMap.name}
        </Header>

        {/* Basic Info Section */}
        <Container header={<Header variant="h2">Basic Information</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <KeyValuePairs
              columns={1}
              items={[
                {
                  label: 'ID',
                  value: baseMap.id
                },
                {
                  label: 'Name',
                  value: baseMap.name
                },
                {
                  label: 'Type',
                  value: baseMap.type === 'single-tile' ? 'Single Tile' : 'Multi-Tile'
                },
                {
                  label: 'Description',
                  value: baseMap.description || 'N/A'
                },
                {
                  label: 'Status',
                  value: baseMap.active ? (
                    <StatusIndicator type="success">Active</StatusIndicator>
                  ) : (
                    <StatusIndicator type="stopped">Inactive</StatusIndicator>
                  )
                }
              ]}
            />
            <KeyValuePairs
              columns={1}
              items={[
                {
                  label: 'Created',
                  value: formatDate(baseMap.createdAt)
                },
                {
                  label: 'Last Updated',
                  value: formatDate(baseMap.updatedAt)
                },
                {
                  label: 'Version',
                  value: baseMap.version.toString()
                },
                {
                  label: 'Update Cycle',
                  value: baseMap.updateCycle || 'N/A'
                },
                {
                  label: 'DPI',
                  value: baseMap.dpi?.toString() || 'N/A'
                }
              ]}
            />
          </ColumnLayout>
        </Container>

        {/* Files and URLs Section */}
        <Container header={<Header variant="h2">Files and URLs</Header>}>
          <KeyValuePairs
            columns={1}
            items={[
              {
                label: 'Thumbnail Path',
                value: baseMap.thumbnailPath
              },
              ...(baseMap.type === 'single-tile'
                ? [
                    {
                      label: 'Overview URL',
                      value: baseMap.overviewUrl || 'N/A'
                    },
                    {
                      label: 'GeoTIFF URL',
                      value: baseMap.geoTiffUrl || 'N/A'
                    }
                  ]
                : [
                    {
                      label: 'Tile Index Path',
                      value: baseMap.tileIndexPath || 'N/A'
                    }
                  ])
            ]}
          />
        </Container>

        {/* Geographic Coverage Section */}
        {baseMap.bounds && (
          <Container header={<Header variant="h2">Geographic Coverage</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <KeyValuePairs
                columns={1}
                items={[
                  {
                    label: 'North Latitude',
                    value: `${baseMap.bounds.north.toFixed(4)}°`
                  },
                  {
                    label: 'South Latitude',
                    value: `${baseMap.bounds.south.toFixed(4)}°`
                  }
                ]}
              />
              <KeyValuePairs
                columns={1}
                items={[
                  {
                    label: 'East Longitude',
                    value: `${baseMap.bounds.east.toFixed(4)}°`
                  },
                  {
                    label: 'West Longitude',
                    value: `${baseMap.bounds.west.toFixed(4)}°`
                  }
                ]}
              />
            </ColumnLayout>
          </Container>
        )}

        {/* Projection Parameters Section */}
        <Container header={<Header variant="h2">Projection Parameters</Header>}>
          <KeyValuePairs
            columns={2}
            items={[
              {
                label: 'Projection Type',
                value: getProjectionTypeName(baseMap.projection.type)
              },
              ...(baseMap.projection.type === 'lcc'
                ? [
                    {
                      label: 'Central Meridian',
                      value: `${baseMap.projection.centralMeridian?.toFixed(4)}°` || 'N/A'
                    },
                    {
                      label: 'Origin Latitude',
                      value: `${baseMap.projection.originLatitude?.toFixed(4)}°` || 'N/A'
                    },
                    {
                      label: 'Standard Parallel 1',
                      value: `${baseMap.projection.standardParallel1?.toFixed(4)}°` || 'N/A'
                    },
                    {
                      label: 'Standard Parallel 2',
                      value: `${baseMap.projection.standardParallel2?.toFixed(4)}°` || 'N/A'
                    }
                  ]
                : []),
              {
                label: 'Datum',
                value: baseMap.datum || 'N/A'
              }
            ]}
          />
        </Container>

        {/* Version History Section */}
        {baseMap.previousVersions && baseMap.previousVersions.length > 0 && (
          <Container header={<Header variant="h2">Version History (Last 10 Versions)</Header>}>
            <Table
              columnDefinitions={[
                {
                  id: 'version',
                  header: 'Version',
                  cell: (item) => item.version
                },
                {
                  id: 'timestamp',
                  header: 'Timestamp',
                  cell: (item) => formatDate(item.timestamp)
                },
                {
                  id: 'updatedBy',
                  header: 'Updated By',
                  cell: (item) => item.updatedBy
                },
                {
                  id: 'actions',
                  header: 'Actions',
                  cell: (item) => (
                    <Button 
                      variant="inline-link" 
                      onClick={() => handleRollbackClick(item)}
                    >
                      Rollback
                    </Button>
                  )
                }
              ]}
              items={baseMap.previousVersions.slice(0, 10)}
              empty={
                <Box textAlign="center" color="inherit">
                  No version history available
                </Box>
              }
            />
          </Container>
        )}
      </SpaceBetween>

      <DeleteConfirmationModal
        visible={showDeleteModal}
        baseMapName={baseMap.name}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Rollback Confirmation Modal - Requirements: 40.3, 40.4 */}
      {selectedVersion && (
        <Alert
          visible={showRollbackModal}
          type="warning"
          header="Confirm Rollback"
          dismissible
          onDismiss={() => {
            setShowRollbackModal(false);
            setSelectedVersion(null);
          }}
          action={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => {
                setShowRollbackModal(false);
                setSelectedVersion(null);
              }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleRollback}>
                Confirm Rollback
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="s">
            <Box>
              Are you sure you want to rollback to version {selectedVersion.version}?
            </Box>
            <Box>
              This will create a new version with the configuration from version {selectedVersion.version} 
              (updated on {formatDate(selectedVersion.timestamp)}).
            </Box>
            <Box variant="strong">
              The current version will be preserved in the version history.
            </Box>
          </SpaceBetween>
        </Alert>
      )}
    </>
  );
};

export default BaseMapDetails;
